import { createSelector } from '@reduxjs/toolkit'
import type { DraftState, SheetState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { baseNameOf, comparableFileName, sampleStemOf } from '../../../Util/fileNames.ts'
import { sequenceIdFits } from '../../../Util/SampleFileUtils.ts'

export type SheetMatch = {
  /** Group key → sheet row index. */
  byGroup: Record<string, number>
  /** How each matched row was matched. */
  via: Record<number, 'file' | 'sample id' | 'sample name'>
  unmatchedRows: number[]
  unmatchedGroups: string[]
  /** Rows that fit more than one sample, which would be a guess. */
  ambiguousRows: number[]
  /** How many samples each ambiguous row fits. */
  ambiguousFits: Record<number, number>
}

type Candidate = { groupKey: string; fileNames: string[]; stem: string; forwardName: string }

const candidatesOf = (draft: Pick<DraftState, 'groups' | 'files'>): Candidate[] =>
  Object.values(draft.groups).map((group) => {
    const files = [group.forward, group.reverse].flatMap((key) => (key && draft.files[key] ? [draft.files[key].name] : []))
    return {
      groupKey: group.key,
      fileNames: files.map(comparableFileName),
      stem: sampleStemOf(files[0] ?? ''),
      forwardName: baseNameOf(draft.files[group.forward]?.name ?? '')
    }
  })

const BCL_TAIL = /_s\d+(_l\d{3})?$/

/**
 * A row value names a stem when it equals it or is it up to a separator, so `S1` never names `S10`. A
 * sequence id with the bcl2fastq tail (`Pond_S1_L001`) names the stem too: the classic import accepts it.
 */
export const namesStem = (value: string, stem: string) => {
  const v = value.trim().toLowerCase().replaceAll('-', '_')
  const names = (candidate: string) =>
    candidate !== '' && (candidate === stem || (stem.startsWith(candidate) && /[._]/.test(stem.charAt(candidate.length))))
  return names(v) || (BCL_TAIL.test(v) && names(v.replace(BCL_TAIL, '')))
}

/**
 * Sheet rows to the draft's samples: by file name first, then sample id, then sample name, each only
 * when it points at exactly one sample. A sample claimed by two rows keeps the first. Rows left over
 * then try their sample id as a prefix of the forward file name, as the classic import does, once every
 * row had its exact try: a prefix never takes a sample another row names.
 */
export const matchSheet = (sheet: SheetState, draft: Pick<DraftState, 'groups' | 'files'>): SheetMatch => {
  const candidates = candidatesOf(draft)
  const { mapping } = sheet
  const match: SheetMatch = { byGroup: {}, via: {}, unmatchedRows: [], unmatchedGroups: [], ambiguousRows: [], ambiguousFits: {} }
  const claimed = new Set<string>()
  /* rows naming a sample an earlier row already took */
  const duplicates = new Set<number>()

  sheet.rows.forEach((row, index) => {
    const tries: [SheetMatch['via'][number], (c: Candidate) => boolean][] = []
    const files = [mapping.fileForward, mapping.fileReverse]
      .map((column) => (column ? row[column] : ''))
      .filter((v): v is string => !!v?.trim())
    if (files.length > 0) {
      tries.push(['file', (c) => files.some((f) => c.fileNames.includes(comparableFileName(f)) || sampleStemOf(f) === c.stem)])
    }
    const id = mapping.sampleId ? row[mapping.sampleId] : ''
    if (id?.trim()) {
      tries.push(['sample id', (c) => namesStem(id, c.stem)])
    }
    const name = mapping.sampleName ? row[mapping.sampleName] : ''
    if (name?.trim()) {
      tries.push(['sample name', (c) => namesStem(name, c.stem)])
    }
    for (const [via, fits] of tries) {
      const hits = candidates.filter(fits)
      if (hits.length === 1) {
        if (!claimed.has(hits[0].groupKey)) {
          claimed.add(hits[0].groupKey)
          match.byGroup[hits[0].groupKey] = index
          match.via[index] = via
        } else {
          match.unmatchedRows.push(index)
          duplicates.add(index)
        }
        return
      }
      if (hits.length > 1) {
        match.ambiguousRows.push(index)
        match.ambiguousFits[index] = hits.length
        return
      }
    }
    match.unmatchedRows.push(index)
  })
  const idColumn = mapping.sampleId
  if (idColumn) {
    match.unmatchedRows = match.unmatchedRows.filter((index) => {
      if (duplicates.has(index)) {
        return true
      }
      const id = sheet.rows[index]?.[idColumn] ?? ''
      const hits = id.trim() ? candidates.filter((c) => sequenceIdFits(id, c.forwardName)) : []
      if (hits.length === 1 && !claimed.has(hits[0].groupKey)) {
        claimed.add(hits[0].groupKey)
        match.byGroup[hits[0].groupKey] = index
        match.via[index] = 'sample id'
        return false
      }
      if (hits.length > 1) {
        match.ambiguousRows.push(index)
        match.ambiguousFits[index] = hits.length
        return false
      }
      return true
    })
    match.ambiguousRows.sort((a, b) => a - b)
  }
  match.unmatchedGroups = candidates.map((c) => c.groupKey).filter((key) => !claimed.has(key))
  return match
}

/**
 * The draft's sheet match, computed once per sheet, groups and files: labels, names, questions and
 * warnings all look a sample up in it, and matching per sample made a few hundred samples take seconds.
 */
export const sheetMatchOf = createSelector(
  [(draft: DraftState) => draft.sheet, (draft: DraftState) => draft.groups, (draft: DraftState) => draft.files],
  (sheet, groups, files): SheetMatch | undefined => (sheet ? matchSheet(sheet, { groups, files }) : undefined)
)
